<template>
    <div class="relative">
        <div class="mb-6 flex items-center justify-between">
            <div class="space-y-1">
                <ULink v-if="!isFieldMedic" to="/field-posts" class="flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700">
                    <span>Field Posts</span>
                    <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                </ULink>
                <div v-if="post" class="flex items-center space-x-4">
                    <h1 class="text-3xl font-semibold text-gray-700">{{ post.name }}</h1>
                    <UBadge v-if="!post.is_active" color="gray" variant="subtle" label="Inactive" />
                </div>
            </div>

            <div v-if="post && canWrite" class="flex items-center gap-x-2">
                <UButton :to="`/field-posts/${post.id}/interventions/new`" color="black" icon="i-ph-plus-bold" label="New Intervention" />
            </div>
        </div>

        <div v-if="post" class="relative flex items-start gap-4">
            <div class="sticky top-0 flex-shrink-0 w-96 space-y-4">
                <div class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Details</h3>
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <Field label="Code">
                            <span class="font-mono">{{ post.code }}</span>
                        </Field>
                        <Field label="Areas">
                            <span v-if="post.areas?.length">{{ post.areas.join(', ') }}</span>
                        </Field>
                        <Field label="Storage Spots">
                            <span v-if="post.storages?.length">{{ post.storages.map(s => s.name).join(', ') }}</span>
                        </Field>
                        <Field label="Status">
                            <BadgeActiveStatus :active="post.is_active" />
                        </Field>
                        <Field v-if="canReadTeam" label="Team">
                            <span v-if="team.length">{{ team.map(m => m.name).join(', ') }}</span>
                        </Field>
                    </div>
                </div>
            </div>

            <div class="flex-1 min-w-0">
                <div class="flex flex-col bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                    <UTabs
                        v-model="selectedTab"
                        class="flex-shrink-0"
                        :items="tabs"
                        :ui="tabsUi"
                    >
                        <template #default="{ item }">
                            <div class="flex items-center space-x-2">
                                <UIcon :name="item.icon" class="w-4 h-4 flex-shrink-0" />
                                <span>{{ item.label }}</span>
                            </div>
                        </template>
                    </UTabs>

                    <div class="flex-1 p-5">
                        <FieldPostInterventionsTab v-if="tabs[selectedTab].key === 'interventions'" :post="post" />
                        <FieldPostInventoryTab v-else-if="tabs[selectedTab].key === 'inventory'" :post="post" :can-write="canWrite" />
                        <FieldPostEquipmentTab v-else :post="post" :can-write="canWrite" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { FIELD_POST_MANAGE_ROLES, FIELD_POST_READ_ROLES, FIELD_POST_WRITE_ROLES, type FieldPost, type TeamMember } from '~/types/fieldPost'

definePageMeta({ layout: 'default', middleware: 'auth', roles: FIELD_POST_READ_ROLES })

const route = useRoute()
const router = useRouter()
const client = useSupabaseClient()
const auth = useAuthStore()
const toast = useToast()

const post = ref<FieldPost>()
const team = ref<TeamMember[]>([])
const isFieldMedic = computed(() => !!auth.hasRole(UserRole.fieldMedic))
const canWrite = computed(() => !!auth.hasRole(...FIELD_POST_WRITE_ROLES))
const canReadTeam = computed(() => !!auth.hasRole(...FIELD_POST_MANAGE_ROLES, UserRole.fieldMedic))

const tabs = [
    { key: 'interventions', label: 'Interventions', icon: 'i-ph-first-aid-duotone' },
    { key: 'inventory', label: 'Inventory', icon: 'i-ph-package-duotone' },
    { key: 'equipment', label: 'Equipment', icon: 'i-ph-heartbeat-duotone' },
]

const tabsUi = {
    wrapper: 'space-y-0 pt-1',
    container: 'border-b border-gray-300',
    list: {
        base: '!flex space-x-4 w-full overflow-x-auto px-1',
        background: 'bg-transparent',
        rounded: 'rounded-none',
        padding: 'p-0',
        height: 'h-auto',
        marker: {
            base: 'w-auto',
            wrapper: 'border-b-2 border-primary-600',
            background: 'bg-transparent',
            rounded: 'rounded-none',
            shadow: 'shadow-none',
        },
        tab: {
            base: 'h-auto w-auto',
            padding: 'px-3 py-3.5',
        },
    },
}

const selectedTab = computed({
    get() {
        const index = tabs.findIndex(t => t.key === route.query.tab)
        return index === -1 ? 0 : index
    },
    set(index: number) {
        router.replace({ query: { ...route.query, tab: tabs[index].key } })
    },
})

useHead({ title: computed(() => post.value?.name ?? 'Field Post') })

onMounted(async () => {
    const postId = route.params.id as string
    const [postRes, membersRes] = await Promise.all([
        client
            .from('field_posts')
            .select('*, storages:field_post_storages (*)')
            .eq('id', postId)
            .single(),
        canReadTeam.value
            ? client.from('field_post_members').select('user:users (id, name)').eq('post_id', postId)
            : Promise.resolve({ data: [] }),
    ])

    if (!postRes.data) {
        toast.add({ title: 'Field post not found.', color: 'red', icon: 'i-ph-x-circle-duotone' })
        await navigateTo('/field-posts', { replace: true })
        return
    }

    const loaded = postRes.data as FieldPost
    loaded.storages = (loaded.storages ?? []).filter(s => !s.is_archived).sort((a, b) => a.sort_order - b.sort_order)
    post.value = loaded

    team.value = ((membersRes.data ?? []) as { user: TeamMember | null }[])
        .map(m => m.user)
        .filter((u): u is TeamMember => !!u)
        .sort((a, b) => a.name.localeCompare(b.name))
})
</script>
